import React, { useState } from "react";
import { useSelector } from "react-redux";
import Button from "../../../../components/staff/Button";
import InputText from "../../../../components/staff/InputText";
import DropDownMultiple from "../../../../components/staff/DropDownMultiple";
import ReadOnlyField from "../../../../components/staff/ReadOnlyField";
import ToogleSwitch from "../../../../components/staff/ToogleSwitch";
import { PaymentMethod, statusEnum } from "../../../../Enums/RegistrationEnum";
import { formatCurrencyOrZero, formatToCurrency } from "../formatters";
import { useEditParticipantForm } from "../hooks/useEditParticipantForm";
import { useEditParticipantSave } from "../hooks/useEditParticipantSave";
import {
  selectDerivedPaymentStatus,
  selectIsEditMode,
  selectRegistrationData,
  selectTotalEventFee,
} from "../editParticipant.selectors";

export default function RegistrantPaymentSection (): React.ReactElement {
  const isEditMode = useSelector(selectIsEditMode);
  const registrationData = useSelector(selectRegistrationData);
  const totalEventFee = useSelector(selectTotalEventFee);
  const derivedPaymentStatus = useSelector(selectDerivedPaymentStatus);
  const {
    paymentMethodOptions,
    payableBalance,
    additionalFeeInput,
    refunded,
    setRefunded,
    amountRefunded,
    handlePaymentMethodChange,
  } = useEditParticipantForm();
  const [refundMethod, setRefundMethod] = useState<PaymentMethod | null>(null);
  const { handleCopyPaymentLink } = useEditParticipantSave();
  const onlyNoCharge =
    paymentMethodOptions.length === 1 &&
    paymentMethodOptions[0]?.id === "noCharge";

  return (
    <div className="flex flex-col gap-4">
      <h3 className="w-full text-secondary">
        Registrant Payment Status
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <DropDownMultiple
          title="Payment Method"
          options={paymentMethodOptions}
          value={
            registrationData?.paymentMethod ??
            (onlyNoCharge ? "noCharge" : null)
          }
          onChange={(option) => {
            const next = option?.id as PaymentMethod | undefined;
            if (next) handlePaymentMethodChange(next);
          }}
          required
          disabled={isEditMode}
          showAsLabel={isEditMode}
        />
        <ReadOnlyField
          label="Payment Status"
          value={statusEnum[derivedPaymentStatus]}
        />
        <ToogleSwitch
          label="Refund"
          labelTop
          checked={refunded}
          disabled
          onChange={(checked) => {
            setRefunded(checked);
            if (!checked) {
              setRefundMethod(null);
            }
          }}
        />
        <InputText
          title="Amount Refunded"
          type="number"
          value={(amountRefunded ?? "").trim() ? amountRefunded : "0"}
          onChange={() => { }}
          prefix="$"
          disabled
          showAsLabel
        />
        <DropDownMultiple
          title="Refund Method"
          options={paymentMethodOptions}
          value={refunded ? refundMethod : null}
          onChange={(option) => {
            const next = option?.id as PaymentMethod | undefined;
            setRefundMethod(next ?? null);
          }}
          disabled={!refunded}
          showAsLabel={!refunded}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <ReadOnlyField
          label="Total Event Fee"
          value={formatCurrencyOrZero(totalEventFee)}
        />
        <ReadOnlyField
          label="Amount Paid"
          value={formatToCurrency(registrationData?.amountPaid)}
        />
        <ReadOnlyField
          label="Total Due (Balance)"
          value={formatCurrencyOrZero(payableBalance)}
        />
        <InputText
          title="Additional Fees"
          type="number"
          value={additionalFeeInput.trim() ? additionalFeeInput : "0"}
          onChange={() => { }}
          prefix="$"
          min={0}
          disabled
          showAsLabel
        />
        <div className="flex min-w-0 flex-col gap-1">
          <label className="font-semibold">Payment Link</label>
          <Button
            text="Copy Payment Link"
            onClick={() => {
              void handleCopyPaymentLink();
            }}
            color="secondary"
            variant="outlined"
            size="fixed"
            className="whitespace-normal text-center"
            disabled={!registrationData?.id}
          />
        </div>
      </div>
    </div>
  );
}
